/**
 * Template Selection Modal Component
 * Modal for selecting slide templates from the hero page
 */

import { useState, useEffect, useRef } from 'react';
import { Presentation, Loader2, AlertCircle, Image as ImageIcon, Palette, Check, ChevronDown } from 'lucide-react';
import { templateAPI, looksAPI, type SavedLook } from '@/services/api';
import { TemplateCard } from './TemplateCard';
import { LookGallery } from './LookGallery';
import { lookName } from '@/lib/studioB/takeover';
import { ParentTemplateCard } from './ParentTemplateCard';
import { TemplateItem } from '@/types/template';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import DESIGN_PACKS_CATALOG from '@/generated/design-packs-catalog.json';
import DESIGN_PACKS_EDU_CATALOG from '@/generated/design-packs-edu-catalog.json';
import { packLabel, visibleDesignPacks, type PackVariant } from '@/lib/designPackAccess';
import { useUserStore } from '@/stores/userStore';
import { useKubicleAllowed } from '@/hooks/useKubicleAllowed';

// Design packs — generated from .claude/design-packs/*/pack.json by
// tools/render-design-packs.mjs. Do not edit the catalogs by hand.
// The video catalog feeds the audio-first pipeline; the edu catalog feeds interactive-edu.
// A card is one of the 18 styles (or a client brand kit); `variants` are the packs folded into
// the style that a viewer can still pick by their own id.
export interface DesignPackItem {
  id: string;
  name: string;
  whenToUse: string;
  colors: string[];
  mode: string;
  rig?: string;
  previewImages: string[];
  requiresFeature?: string;
  packId?: string;
  variants?: PackVariant[];
  aliases?: string[];
  shelf?: { place: 'first' | 'more'; bestFor: string; fits: string[]; clipTheme: string };
}
const DESIGN_PACKS_VIDEO = DESIGN_PACKS_CATALOG as DesignPackItem[];
const DESIGN_PACKS_EDU = DESIGN_PACKS_EDU_CATALOG as DesignPackItem[];

interface TemplateSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTemplateSelect: (templateId: string, templateName: string, previewUrl?: string, colorPalette?: ColorPalette, designPackId?: string, lookName?: string) => void;
  selectedTemplateId: string | null;
  /**
   * Selection mode:
   * - 'layout': Select individual layouts (for slide workflow) - shows expandable parents
   * - 'theme': Select parent theme only (for video workflow) - clicking parent selects it
   */
  selectionMode?: 'layout' | 'theme';
  /** Open directly to design system picker (skip template list) */
  designSystemOnly?: boolean;
  /** No "Default colors" control: opened from a storyboard, where only the look is being chosen. */
  hideColors?: boolean;
}

// Legacy flat template interface for backward compatibility
interface LegacyTemplateData {
  id: string;
  name: string;
  path: string;
  relativePath: string;
  slideJsonUrl: string;
  previewUrl?: string;
  metadata?: any;
}

// Color mode preference for design systems

// Semantic color roles mapped from the palette
export interface PaletteRoles {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

// Trending color palettes (Pantone 2025-26, Canva, design trends)
export interface ColorPalette {
  id: string;
  name: string;
  colors: string[];
  roles: PaletteRoles;
  source: string;
  description: string;
}

export const COLOR_PALETTES: ColorPalette[] = [
  // Pantone 2025-26
  {
    id: 'mocha-mousse',
    name: 'Mocha Mousse',
    colors: ['#A47864', '#F7E8E1', '#E6C2B2', '#683B39', '#31231A'],
    roles: { primary: '#A47864', secondary: '#E6C2B2', accent: '#683B39', background: '#F7E8E1', text: '#31231A' },
    source: 'Pantone 2025',
    description: 'Warm, indulgent browns with creamy neutrals and deep cocoa.',
  },
  {
    id: 'cloud-dancer',
    name: 'Cloud Dancer',
    colors: ['#1E4CA1', '#854C65', '#015871', '#F0EEE9', '#825E2F'],
    roles: { primary: '#1E4CA1', secondary: '#854C65', accent: '#015871', background: '#F0EEE9', text: '#825E2F' },
    source: 'Pantone 2026',
    description: 'Canvas-white with deep cobalt, damson, bronze, and teal.',
  },
  {
    id: 'fw-warm',
    name: 'Autumn Harvest',
    colors: ['#BE394F', '#D1805F', '#DCD494', '#8F3D37', '#683B39'],
    roles: { primary: '#BE394F', secondary: '#D1805F', accent: '#DCD494', background: '#8F3D37', text: '#683B39' },
    source: 'Pantone FW 25/26',
    description: 'Lemongrass through brandied melon to deep chili oil.',
  },
  {
    id: 'fw-cool',
    name: 'Nordic Jewel',
    colors: ['#015871', '#854C65', '#DC343B', '#EED4D9', '#825E2F'],
    roles: { primary: '#015871', secondary: '#854C65', accent: '#DC343B', background: '#EED4D9', text: '#825E2F' },
    source: 'Pantone FW 25/26',
    description: 'Lyons blue and damson plum balanced with metallic warmth.',
  },
  // Trending design palettes
  {
    id: 'apricot-lavender',
    name: 'Apricot & Lavender',
    colors: ['#E99D57', '#B497BD', '#5B7553', '#F0EEE9', '#F9D1B5'],
    roles: { primary: '#E99D57', secondary: '#B497BD', accent: '#5B7553', background: '#F0EEE9', text: '#F9D1B5' },
    source: 'Trending',
    description: 'Soft-bold apricot and lavender duo with eco green.',
  },
  {
    id: 'cosmic-gradient',
    name: 'Cosmic Gradient',
    colors: ['#E8D9FF', '#FFD7E8', '#A9E1FF', '#F0EEE9', '#C9F2FF'],
    roles: { primary: '#E8D9FF', secondary: '#FFD7E8', accent: '#A9E1FF', background: '#F0EEE9', text: '#C9F2FF' },
    source: 'Trending',
    description: 'Iridescent pastels — lilac, rose, ice blue, sky.',
  },
  {
    id: 'digital-calm',
    name: 'Digital Calm',
    colors: ['#B497BD', '#AADBC8', '#FFE066', '#F0EEE9', '#1E4CA1'],
    roles: { primary: '#B497BD', secondary: '#AADBC8', accent: '#FFE066', background: '#F0EEE9', text: '#1E4CA1' },
    source: 'Trending',
    description: 'Lavender and mint pixel on cloud white with golden accent.',
  },
  {
    id: 'neon-depths',
    name: 'Neon Depths',
    colors: ['#FF6347', '#1E4CA1', '#FFE066', '#0A0A0A', '#F0EEE9'],
    roles: { primary: '#FF6347', secondary: '#1E4CA1', accent: '#FFE066', background: '#0A0A0A', text: '#F0EEE9' },
    source: 'Trending',
    description: 'Coral red and cobalt against near-black with golden pop.',
  },
  // Classic / editorial palettes
  {
    id: 'earthy-luxe',
    name: 'Earthy Luxe',
    colors: ['#4E342E', '#825E2F', '#AD974F', '#EAEAEA', '#231F20'],
    roles: { primary: '#4E342E', secondary: '#825E2F', accent: '#AD974F', background: '#EAEAEA', text: '#231F20' },
    source: 'Classic',
    description: 'Chocolate, bronze, and muted gold — premium and grounded.',
  },
  {
    id: 'dusty-mint',
    name: 'Dusty Mint',
    colors: ['#AADBC8', '#B497BD', '#3D6B59', '#F0EEE9', '#E0E7E3'],
    roles: { primary: '#AADBC8', secondary: '#B497BD', accent: '#3D6B59', background: '#F0EEE9', text: '#E0E7E3' },
    source: 'Trending',
    description: 'Clinical greens softened with lavender and cloud white.',
  },
  {
    id: 'mustard-teal',
    name: 'Mustard & Teal',
    colors: ['#D4A843', '#015871', '#D1805F', '#F0EEE9', '#2B2B2B'],
    roles: { primary: '#D4A843', secondary: '#015871', accent: '#D1805F', background: '#F0EEE9', text: '#2B2B2B' },
    source: 'Classic',
    description: 'Mid-century mustard and deep teal with mocha warmth.',
  },
  {
    id: 'soft-rose',
    name: 'Soft Rose & Slate',
    colors: ['#854C65', '#EED4D9', '#A47864', '#F0EEE9', '#4A4E57'],
    roles: { primary: '#854C65', secondary: '#EED4D9', accent: '#A47864', background: '#F0EEE9', text: '#4A4E57' },
    source: 'Classic',
    description: 'Primrose pink and damson plum against cool slate grey.',
  },
  {
    id: 'golden-regal',
    name: 'Golden Regal',
    colors: ['#F1C232', '#CE7E00', '#C90076', '#6A329F', '#8E7CC3'],
    roles: { primary: '#F1C232', secondary: '#CE7E00', accent: '#C90076', background: '#6A329F', text: '#8E7CC3' },
    source: 'Classic',
    description: 'Warm gold and deep amber with magenta and violet.',
  },
  {
    id: 'atlas-moth',
    name: 'Atlas Moth',
    colors: ['#924622', '#9E7967', '#35170C', '#0E1B07', '#F7E8E1'],
    roles: { primary: '#924622', secondary: '#9E7967', accent: '#35170C', background: '#0E1B07', text: '#F7E8E1' },
    source: 'Pantone 2025',
    description: 'Dark forest and burnt umber — organic, natural camouflage.',
  },
];

// Group palettes by source for display
const PALETTE_GROUPS: { label: string; sources: string[] }[] = [
  { label: 'Pantone', sources: ['Pantone 2025', 'Pantone 2026', 'Pantone FW 25/26'] },
  { label: 'Trending', sources: ['Trending'] },
  { label: 'Classic', sources: ['Classic'] },
];

// Templates visible on the hero page, in display order
const HERO_TEMPLATE_ORDER = [
  'blank-canvas',
  'navy-orange-pitch',
  'intro1',
  'navy-gold-corporate',
  'blue-venture',
  'pink-business',
  'teal-pitch',
  'warm-business-beige-purple-yellow',
  'kpi_v1',
];
const HERO_VISIBLE_TEMPLATES = new Set(HERO_TEMPLATE_ORDER);


export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  open,
  onOpenChange,
  onTemplateSelect,
  selectedTemplateId,
  selectionMode = 'layout',
  designSystemOnly = false,
  hideColors = false,
}) => {
  const features = useUserStore((s) => s.features);
  const kubicleAllowed = useKubicleAllowed();
  const granted = kubicleAllowed ? [...features, 'kubicle'] : features;
  const VIDEO_LOOKS = visibleDesignPacks(DESIGN_PACKS_VIDEO, granted);
  const LESSON_LOOKS = visibleDesignPacks(DESIGN_PACKS_EDU, granted);
  const EVERY_LOOK = [...VIDEO_LOOKS, ...LESSON_LOOKS];
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(new Set());
  // Design system sub-selector for blank-canvas
  const [showDesignSystemPicker, setShowDesignSystemPicker] = useState(false);
  // null: Ivy picks by content. Picking a look sets it.
  const [selectedDesignPack, setSelectedDesignPack] = useState<string | null>(null);
  const [savedLooks, setSavedLooks] = useState<SavedLook[]>([]);
  const [selectedPaletteId, setSelectedPaletteId] = useState<string | null>(null);
  const [paletteDropdownOpen, setPaletteDropdownOpen] = useState(false);
  const paletteDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && designSystemOnly) looksAPI.mine().then(setSavedLooks).catch(() => setSavedLooks([]));
  }, [open, designSystemOnly]);

  useEffect(() => {
    if (open) {
      if (designSystemOnly) {
        setShowDesignSystemPicker(true);
      } else {
        fetchTemplates();
        setShowDesignSystemPicker(false);
      }
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await templateAPI.listPublicTemplates();

      if (response.success) {
        // Filter to only show whitelisted templates, sorted by display order
        const filtered = response.templates
          .filter((t: TemplateItem) => HERO_VISIBLE_TEMPLATES.has(t.id))
          .sort((a: TemplateItem, b: TemplateItem) => {
            const aIdx = HERO_TEMPLATE_ORDER.indexOf(a.id);
            const bIdx = HERO_TEMPLATE_ORDER.indexOf(b.id);
            return aIdx - bIdx;
          });
        setTemplates(filtered);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle selection from child layouts within expanded parent
  const handleChildLayoutClick = (template: LegacyTemplateData) => {
    // Extract template name from ID (last part after /)
    const templateName = template.name || template.id.split('/').pop() || template.id;

    onTemplateSelect(template.id, templateName, template.previewUrl);
    onOpenChange(false);
  };

  // Legacy handler for standalone template cards
  const handleLegacyTemplateClick = (template: LegacyTemplateData) => {
    // If blank-canvas selected, show design system picker instead of closing
    if (template.id === 'blank-canvas') {
      setShowDesignSystemPicker(true);
      return;
    }
    onTemplateSelect(template.id, template.name, template.previewUrl);
    onOpenChange(false);
  };

  // Handle parent theme selection (for video workflow)
  const handleParentThemeSelect = (template: TemplateItem) => {
    if (template.type !== 'parent') return;

    // Use the first layout's ID but with parent prefix for theme selection
    // Format: "parentId/layout_01" -> just use parentId for theme
    const themeId = template.id;
    const themeName = template.name;
    // Use the first layout's preview as the theme preview, fallback to parent's previewImage
    const previewUrl = template.layouts[0]?.previewUrl || template.previewImage;

    // If blank-canvas selected, show design system picker instead of closing
    if (themeId === 'blank-canvas') {
      setShowDesignSystemPicker(true);
      return;
    }

    onTemplateSelect(themeId, themeName, previewUrl);
    onOpenChange(false);
  };

  // Handle design system confirmation for blank-canvas
  const handleDesignSystemConfirm = () => {
    const palette = selectedPaletteId ? COLOR_PALETTES.find(p => p.id === selectedPaletteId) : undefined;
    if (selectedDesignPack) {
      const name = packLabel(EVERY_LOOK, selectedDesignPack) ?? lookName(selectedDesignPack);
      onTemplateSelect('blank-canvas', `Blank Canvas (${name})`, undefined, palette, selectedDesignPack, name);
    } else {
      // Ivy picks: no look id at all, so the pipeline chooses by content
      onTemplateSelect('blank-canvas', 'Blank Canvas (Auto style)', undefined, palette);
    }
    setShowDesignSystemPicker(false);
    onOpenChange(false);
  };

  // Toggle parent expansion (works in both layout and theme modes)
  const toggleParentExpansion = (parentId: string) => {
    setExpandedParentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };

  // Count total templates (parent + standalone)
  const totalTemplateCount = templates.reduce((count, item) => {
    if (item.type === 'parent') {
      return count + item.layouts.length;
    }
    return count + 1;
  }, 0);

  // Build grid items with expansion rows
  const gridItems: React.ReactNode[] = [];

  templates.forEach((item, index) => {
    if (item.type === 'parent') {
      // Add parent card
      // In theme mode, parent is directly selectable AND can be expanded to preview layouts
      // In layout mode, parent expands to show selectable layouts
      const isSelected = selectionMode === 'theme' && item.id === selectedTemplateId;
      const isExpanded = expandedParentIds.has(item.id);

      gridItems.push(
        <ParentTemplateCard
          key={item.id}
          template={item}
          isExpanded={isExpanded}
          isSelected={isSelected}
          onToggleExpand={() => toggleParentExpansion(item.id)}
          onSelect={selectionMode === 'theme' ? () => handleParentThemeSelect(item) : undefined}
          selectionMode={selectionMode}
        />
      );

      // Add expansion row if expanded (both modes, but with different behavior)
      if (isExpanded) {
        gridItems.push(
          <div
            key={`${item.id}-expanded`}
            className="col-span-3 md:col-span-4 lg:col-span-5 bg-muted/30 dark:bg-muted/20 rounded-xl p-4 border border-border/50 backdrop-blur-sm"
          >
            {/* Header for theme mode - explain this is preview only */}
            {selectionMode === 'theme' && (
              <p className="text-xs text-muted-foreground mb-3">
                Preview of {item.layouts.length} layouts included in this template:
              </p>
            )}
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {item.layouts.map((layout, layoutIndex) => {
                const legacyTemplate: LegacyTemplateData = {
                  id: layout.id,
                  name: layout.name,
                  path: '',
                  relativePath: '',
                  slideJsonUrl: layout.slideJsonUrl,
                  previewUrl: layout.previewUrl,
                  metadata: layout.metadata,
                };

                if (selectionMode === 'theme') {
                  // In theme mode, layouts are preview-only (not clickable)
                  return (
                    <div
                      key={layout.id}
                      className="relative overflow-hidden rounded-lg border border-border/50 bg-slate-50 dark:bg-slate-900"
                    >
                      <div className="aspect-video flex items-center justify-center p-2">
                        {layout.previewUrl ? (
                          <img
                            src={layout.previewUrl}
                            alt={layout.name}
                            className="max-w-full max-h-full object-contain rounded"
                            loading="lazy"
                          />
                        ) : (
                          <div className="text-center text-muted-foreground">
                            <ImageIcon className="h-8 w-8 mx-auto opacity-50" />
                          </div>
                        )}
                      </div>
                      <div className="px-2 py-1.5 bg-card border-t text-center">
                        <p className="text-xs text-muted-foreground truncate">
                          {layout.name}
                        </p>
                      </div>
                    </div>
                  );
                }

                // In layout mode, layouts are selectable
                return (
                  <TemplateCard
                    key={layout.id}
                    template={legacyTemplate}
                    isSelected={layout.id === selectedTemplateId}
                    onTemplateClick={handleChildLayoutClick}
                    index={layoutIndex}
                  />
                );
              })}
            </div>
          </div>
        );
      }
    } else {
      // Add standalone template
      const legacyTemplate: LegacyTemplateData = {
        id: item.id,
        name: item.name,
        path: '',
        relativePath: '',
        slideJsonUrl: item.slideJsonUrl,
        previewUrl: item.previewUrl,
        metadata: item.metadata,
      };

      gridItems.push(
        <TemplateCard
          key={item.id}
          template={legacyTemplate}
          isSelected={item.id === selectedTemplateId}
          onTemplateClick={handleLegacyTemplateClick}
          index={index}
        />
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] flex flex-col p-0 bg-background/95 backdrop-blur-xl border-border/50">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border/50 bg-gradient-to-b from-muted/30 to-transparent">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 shrink-0 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20`}>
              {designSystemOnly ? <Palette className="h-5 w-5 text-primary-foreground" /> : <Presentation className="h-5 w-5 text-primary-foreground" />}
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                {designSystemOnly ? 'Choose a look' : selectionMode === 'theme' ? 'Choose a Theme' : 'Choose a Template'}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {designSystemOnly
                  ? 'The same story in every look: where your tap water comes from. Ivy draws your own scene at the first stop.'
                  : selectionMode === 'theme'
                  ? 'Select a theme for your video - layouts will be auto-assigned to each slide'
                  : 'Select a template to start your presentation'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Design System Picker */}
          {showDesignSystemPicker && (
            <div className="space-y-4">
              {!designSystemOnly && (
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setShowDesignSystemPicker(false)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    &larr; Back to templates
                  </button>
                </div>
              )}
              {!designSystemOnly && (
                <div className="text-center mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-primary/20 bg-primary">
                    <Palette className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-base font-semibold">Choose a Design Style</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This determines the visual style and layout options for your slides
                  </p>
                </div>
              )}
                  <LookGallery videoCards={VIDEO_LOOKS} lessonCards={LESSON_LOOKS} savedLooks={savedLooks} value={selectedDesignPack} onChange={setSelectedDesignPack} />
            </div>
          )}

          {isLoading && !showDesignSystemPicker && (
            <div className="h-64 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/20">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Loading templates...</p>
              </div>
            </div>
          )}

          {error && !showDesignSystemPicker && (
            <div className="h-64 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 max-w-md text-center">
                <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1">Failed to Load Templates</h3>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
                <button
                  onClick={fetchTemplates}
                  className="mt-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && !showDesignSystemPicker && templates.length === 0 && (
            <div className="h-64 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 max-w-md text-center">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Presentation className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1">No Templates Available</h3>
                  <p className="text-sm text-muted-foreground">
                    No templates are currently available in the library.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !error && !showDesignSystemPicker && templates.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {gridItems}
            </div>
          )}
        </div>

        {showDesignSystemPicker && (
          <div className="px-6 py-3 border-t border-border/50 bg-muted/10">
              {/* Compact toolbar: Palette dropdown + Continue */}
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Color Palette Dropdown (no portal — avoids Dialog scroll conflict) */}
                  {!hideColors && <div className="relative" ref={paletteDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setPaletteDropdownOpen(!paletteDropdownOpen)}
                      className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors text-sm"
                    >
                      <Palette className="h-3.5 w-3.5 text-muted-foreground" />
                      {selectedPaletteId ? (
                        <>
                          <div className="flex gap-0.5">
                            {(() => {
                              const p = COLOR_PALETTES.find(pl => pl.id === selectedPaletteId);
                              if (!p) return null;
                              return (['primary', 'secondary', 'accent', 'background', 'text'] as const).map((role) => (
                                <div
                                  key={role}
                                  className="w-3 h-3 rounded-full border border-black/10 dark:border-white/10"
                                  style={{ backgroundColor: p.roles[role] }}
                                />
                              ));
                            })()}
                          </div>
                          <span className="font-medium truncate max-w-[120px]">
                            {COLOR_PALETTES.find(p => p.id === selectedPaletteId)?.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Default colors</span>
                      )}
                      <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${paletteDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {paletteDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setPaletteDropdownOpen(false)} />
                        <div className="absolute left-0 bottom-full mb-1 z-50 w-[340px] rounded-lg border bg-popover text-popover-foreground shadow-lg">
                          <div
                            className="p-3"
                            style={{ maxHeight: '320px', overflowY: 'auto' }}
                          >
                            {selectedPaletteId && (
                              <button
                                type="button"
                                onClick={() => { setSelectedPaletteId(null); setPaletteDropdownOpen(false); }}
                                className="w-full text-left text-xs text-muted-foreground hover:text-foreground mb-2 pb-2 border-b border-border/30 transition-colors"
                              >
                                Reset to default colors
                              </button>
                            )}
                            {PALETTE_GROUPS.map((group) => {
                              const palettes = COLOR_PALETTES.filter(p => group.sources.includes(p.source));
                              if (palettes.length === 0) return null;
                              return (
                                <div key={group.label} className="mb-2 last:mb-0">
                                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">{group.label}</p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {palettes.map((palette) => (
                                      <button
                                        key={palette.id}
                                        type="button"
                                        onClick={() => { setSelectedPaletteId(selectedPaletteId === palette.id ? null : palette.id); setPaletteDropdownOpen(false); }}
                                        className={`flex items-center gap-2 p-2 rounded-md border transition-all duration-150 text-left ${
                                          selectedPaletteId === palette.id
                                            ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                            : 'border-transparent hover:bg-muted/40'
                                        }`}
                                      >
                                        <div className="flex gap-0.5 flex-shrink-0">
                                          {(['primary', 'secondary', 'accent', 'background', 'text'] as const).map((role) => (
                                            <div
                                              key={role}
                                              className="w-3 h-3 rounded-full border border-black/10 dark:border-white/10"
                                              style={{ backgroundColor: palette.roles[role] }}
                                            />
                                          ))}
                                        </div>
                                        <span className="text-xs font-medium truncate">{palette.name}</span>
                                        {selectedPaletteId === palette.id && (
                                          <Check className="h-3 w-3 text-primary ml-auto flex-shrink-0" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>}

                  {/* Continue Button */}
                  <button
                    type="button"
                    onClick={handleDesignSystemConfirm}
                    className="ml-auto px-5 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
                  >
                    Use {selectedDesignPack ? packLabel(EVERY_LOOK, selectedDesignPack) ?? lookName(selectedDesignPack) : 'Ivy picks'}
                    {selectedPaletteId && ` + ${COLOR_PALETTES.find(p => p.id === selectedPaletteId)?.name}`}
                  </button>
                </div>
              </div>
          </div>
        )}
        {!showDesignSystemPicker && <div className="px-6 py-3 border-t border-border/50 bg-muted/10">
          <p className="text-xs text-muted-foreground text-center">
            {selectionMode === 'theme' ? (
              'Click a design system to select'
            ) : (
              totalTemplateCount > 0 && (
                <>
                  {totalTemplateCount} {totalTemplateCount === 1 ? 'layout' : 'layouts'} available
                  {selectedTemplateId && ' • Click to select'}
                </>
              )
            )}
          </p>
        </div>}
      </DialogContent>
    </Dialog>
  );
};
